const TOKEN_STORAGE_KEY = "auth.token";

function getStoredToken() {
  if (globalThis.localStorage === undefined) return null;
  return globalThis.localStorage.getItem(TOKEN_STORAGE_KEY);
}

function setStoredToken(token) {
  if (globalThis.localStorage === undefined) return;
  if (token) {
    globalThis.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } else {
    globalThis.localStorage.removeItem(TOKEN_STORAGE_KEY);
  }
}

export class ApiError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

async function request(path, { method = "GET", body, headers = {}, raw = false } = {}) {
  const finalHeaders = { Accept: "application/json", ...headers };
  const token = getStoredToken();
  if (token) finalHeaders.Authorization = `Bearer ${token}`;

  let finalBody = body;
  if (body && !(body instanceof FormData) && typeof body !== "string") {
    finalHeaders["Content-Type"] = "application/json";
    finalBody = JSON.stringify(body);
  }

  const response = await fetch(path, {
    method,
    headers: finalHeaders,
    body: finalBody,
    credentials: "same-origin",
  });

  const authHeader = response.headers.get("Authorization");
  if (authHeader?.startsWith("Bearer ")) {
    setStoredToken(authHeader.slice(7));
  }

  if (response.status === 401) {
    setStoredToken(null);
  }

  if (raw) return response;

  const text = await response.text();
  const data = text ? safeJson(text) : null;

  if (!response.ok) {
    throw new ApiError(data?.error || `Request failed: ${response.status}`, {
      status: response.status,
      body: data,
    });
  }

  return data;
}

function safeJson(text) {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const apiClient = {
  get: (path, options) => request(path, { ...options, method: "GET" }),
  post: (path, body, options) => request(path, { ...options, method: "POST", body }),
  put: (path, body, options) => request(path, { ...options, method: "PUT", body }),
  patch: (path, body, options) => request(path, { ...options, method: "PATCH", body }),
  delete: (path, options) => request(path, { ...options, method: "DELETE" }),
  getToken: getStoredToken,
  setToken: setStoredToken,
  clearToken: () => setStoredToken(null),
};
